import { EditableInput } from "../../../styles/SettingsPage.styled";
import { Dropdown } from "antd";
import { tableTitleStyle } from "../../MarketOverviewPage/data/constants";
import { OneFormulaData, MetricCategory, CenteredValueProps, GetOneFormulaColumnsParams } from "../types";
import { getProductTerms } from "../../../hooks/useProductTerm";

// Header accent colour per metric category
const getCategoryColor = (category: MetricCategory): string => {
	switch (category) {
		case "availability":
			return "#d71557"; // rose-600
		case "bundles":
			return "#ff6f00"; // orange-500
		case "content":
			return "#1a73e8"; // blue-600
		case "general":
			return "#6b7280"; // gray-500
		default:
			return "#6b7280";
	}
};

const CenteredValue: React.FC<CenteredValueProps> = ({
	value,
	width = "auto",
}) => (
	<div
		className={`text-center text-xs font-normal px-2.5 py-1 rounded-sm ${width} inline-flex justify-center items-center`}
	>
		{value}
	</div>
);

const getOneFormulaColumns = ({
	renderTooltipCell,
	id_client,
	isEditingOneFormula,
	editingOneFormulaData,
	handleInputChange,
	handleApplyDefaultToEditingRow,
	handleSaveDefaultValue,
	handleApplyToAllClick,
}: GetOneFormulaColumnsParams): any => {
	const { replaceTerm } = getProductTerms(id_client);
	return [
	{
		title: (
			<div className="flex items-center gap-1 justify-left px-2">Market</div>
		),
		dataIndex: "market",
		key: "market",
		width: 100,
		sorter: (a: OneFormulaData, b: OneFormulaData) =>
			a.market.localeCompare(b.market),
		render: (text: string, _record: OneFormulaData) => {
			const isOverrideValue = text.includes("(Override Value)");
			const isDefaultValue = text === "Default Value";
			const marketName = isOverrideValue ? text.replace(" (Override Value)", "") : text;
			
			return (
				<div className="flex flex-col items-start px-1">
					<span
						className={`text-[0.75rem] text-left ${
							isDefaultValue || isOverrideValue ? "font-medium" : ""
						}`}
					>
						{isOverrideValue ? marketName : text}
					</span>
					{isOverrideValue && (
						<span className="text-[0.625rem] text-[#0e49b5] text-left">
							Override Value
						</span>
					)}
				</div>
			);
		},
	},
	{
		title: "Availability",
		children: [
			{
				title: (
					<div className="flex gap-1.5 justify-center items-center">
						<span className="text-[0.75rem]">
							Market Penetration (All){" "}
							{renderTooltipCell(
								"% of digital storefronts (DSFs) in the market that list your brand.",
                "",
								getCategoryColor("availability")
							)}
						</span>
					</div>
				),
				dataIndex: "marketPenetrationOfClient",
				key: "marketPenetrationOfClient",
				width: 110,
				render: (value: number, record: OneFormulaData) => {
					const canEdit = isEditingOneFormula;
					const isOverrideRow = record.key.endsWith('-override');
          const isDefaultRow = record.key === "default-value";
					return (
						<div className="flex items-center justify-center">
							{canEdit ? (
								<EditableInput
									size="small"
									min={0}
									max={100}
									value={
										editingOneFormulaData[record.key]
											?.marketPenetrationOfClient ?? value
									}
									suffix="%"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const numValue = parseInt(e.target.value) || 0;
										handleInputChange(
											record.key,
											"marketPenetrationOfClient",
											numValue
										);
									}}
								/>
							) : (
								<div 
									className="text-center text-xs font-normal px-2.5 py-1 rounded-sm inline-flex justify-center items-center"
									style={isOverrideRow || isDefaultRow ? { fontWeight: 500 } : {fontStyle: "italic"}}
								>
									{value}%
								</div>
							)}
						</div>
					);
				},
			},
			{
				title: (
					<div className="flex gap-1.5 justify-center items-center">
						<span className="text-[0.75rem]">
              {replaceTerm("Market Penetration (Drinks)")}{" "}
							{renderTooltipCell(
								replaceTerm("% of DSFs listing your brand among those that list any drinks."),
                "",
								getCategoryColor("availability")
							)}
						</span>
					</div>
				),
				dataIndex: "marketPenetration",
				key: "marketPenetration",
				width: 110,
				render: (value: number, record: OneFormulaData) => {
					const canEdit = isEditingOneFormula;
          const isOverrideRow = record.key.endsWith('-override');
          const isDefaultRow = record.key === "default-value";
					return (
						<div className="flex items-center justify-center">
							{canEdit ? (
								<EditableInput
									size="small"
									min={0}
									max={100}
									value={
										editingOneFormulaData[record.key]?.marketPenetration ?? value
									}
									suffix="%"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const numValue = parseInt(e.target.value) || 0;
										handleInputChange(record.key, "marketPenetration", numValue);
									}}
								/>
							) : (
								<div 
									className="text-center text-xs font-normal px-2.5 py-1 rounded-sm inline-flex justify-center items-center"
									style={isOverrideRow || isDefaultRow ? { fontWeight: 500 } : {fontStyle: "italic"}}
								>
									{value}%
								</div>
							)}
						</div>
					);
				},
			},
			{
				title: (
					<div className="flex gap-1.5 justify-center items-center">
						<span className="text-[0.75rem]">
							Core SKU Presence (%){" "}
							{renderTooltipCell(
								"Average % of your core brands (1–8) listed per DSF that carries your brand.",
								"",
								getCategoryColor("availability")
							)}
						</span>
					</div>
				),
				dataIndex: "avgCoreSkuClient",
				key: "avgCoreSkuClient",
				width: 120,
				render: (value: number, record: OneFormulaData) => {
					const canEdit = isEditingOneFormula;
          const isOverrideRow = record.key.endsWith('-override');
          const isDefaultRow = record.key === "default-value";
					return (
						<div className="flex items-center justify-center">
							{canEdit ? (
								<EditableInput
									size="small"
									min={0}
									max={100}
									value={
										editingOneFormulaData[record.key]?.avgCoreSkuClient ?? value
									}
									suffix="%"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const numValue = parseInt(e.target.value) || 0;
										handleInputChange(record.key, "avgCoreSkuClient", numValue);
									}}
								/>
							) : (
								<div 
									className="text-center text-xs font-normal px-2.5 py-1 rounded-sm inline-flex justify-center items-center"
									style={isOverrideRow || isDefaultRow ? { fontWeight: 500 } : {fontStyle: "italic"}}
								>
									{value}%
								</div>
							)}
						</div>
					);
				},
			},
		],
		onHeaderCell: () => ({
			style: {
				...tableTitleStyle,
				backgroundColor: getCategoryColor("availability"),
				borderTopLeftRadius: "12px",
			},
		}),
	},
	{
		title: "Bundles",
		children: [
			{
				title: (
					<div className="flex gap-1.5 justify-center items-center">
						<span className="text-[0.75rem]">
							Combo Presence{" "}
							{renderTooltipCell(
								replaceTerm("% of DSFs listing at least one of your drinks in any combo."),
								"",
								getCategoryColor("bundles")
							)}
						</span>
					</div>
				),
				dataIndex: "comboPresence",
				key: "comboPresence",
				width: 90,
				render: (value: number, record: OneFormulaData) => {
					const canEdit = isEditingOneFormula;
          const isOverrideRow = record.key.endsWith('-override');
          const isDefaultRow = record.key === "default-value";
					return (
						<div className="flex items-center justify-center">
							{canEdit ? (
								<EditableInput
									size="small"
									min={0}
									max={100}
									value={
										editingOneFormulaData[record.key]?.comboPresence ?? value
									}
									suffix="%"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const numValue = parseInt(e.target.value) || 0;
										handleInputChange(record.key, "comboPresence", numValue);
									}}
								/>
							) : (
								<div 
									className="text-center text-xs font-normal px-2.5 py-1 rounded-sm inline-flex justify-center items-center"
									style={isOverrideRow || isDefaultRow ? { fontWeight: 500 } : {fontStyle: "italic"}}
								>
									{value}%
								</div>
							)}
						</div>
					);
				},
			},
			{
				title: (
					<div className="flex gap-1.5 justify-center items-center">
						<span className="text-[0.75rem]">
							Fixed Combo Presence{" "}
							{renderTooltipCell(
								replaceTerm("% of DSFs listing your drinks in fixed combos (pre-set bundles)."),
								"",
								getCategoryColor("bundles")
							)}
						</span>
					</div>
				),
				dataIndex: "fixedComboPresence",
				key: "fixedComboPresence",
				width: 95,
				render: (value: number, record: OneFormulaData) => {
					const canEdit = isEditingOneFormula;
          const isOverrideRow = record.key.endsWith('-override');
          const isDefaultRow = record.key === "default-value";
					return (
						<div className="flex items-center justify-center">
							{canEdit ? (
								<EditableInput
									size="small"
									min={0}
									max={100}
									value={
										editingOneFormulaData[record.key]?.fixedComboPresence ?? value
									}
									suffix="%"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const numValue = parseInt(e.target.value) || 0;
										handleInputChange(record.key, "fixedComboPresence", numValue);
									}}
								/>
							) : (
								<div 
									className="text-center text-xs font-normal px-2.5 py-1 rounded-sm inline-flex justify-center items-center"
									style={isOverrideRow || isDefaultRow ? { fontWeight: 500 } : {fontStyle: "italic"}}
								>
									{value}%
								</div>
							)}
						</div>
					);
				},
			},
			{
				title: (
					<div className="flex gap-1.5 justify-center items-center">
						<span className="text-[0.75rem]">
							Option Combo Presence{" "}
							{renderTooltipCell(
								replaceTerm("% of DSFs listing your drinks in optional combos (choice bundles)."),
								"",
								getCategoryColor("bundles")
							)}
						</span>
					</div>
				),
				dataIndex: "optionComboPresence",
				key: "optionComboPresence",
				width: 100,
				render: (value: number, record: OneFormulaData) => {
					const canEdit = isEditingOneFormula;
          const isOverrideRow = record.key.endsWith('-override');
          const isDefaultRow = record.key === "default-value";
					return (
						<div className="flex items-center justify-center">
							{canEdit ? (
								<EditableInput
									size="small"
									min={0}
									max={100}
									value={
										editingOneFormulaData[record.key]?.optionComboPresence ??
										value
									}
									suffix="%"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const numValue = parseInt(e.target.value) || 0;
										handleInputChange(
											record.key,
											"optionComboPresence",
											numValue
										);
									}}
								/>
							) : (
								<div 
									className="text-center text-xs font-normal px-2.5 py-1 rounded-sm inline-flex justify-center items-center"
									style={isOverrideRow || isDefaultRow ? { fontWeight: 500 } : {fontStyle: "italic"}}
								>
									{value}%
								</div>
							)}
						</div>
					);
				},
			},
		],
		onHeaderCell: () => ({
			style: {
				...tableTitleStyle,
				backgroundColor: getCategoryColor("bundles"),
			},
		}),
	},
	{
		title: "Content",
		children: [
			{
				title: (
					<div className="flex gap-1.5 justify-center items-center">
						<span className="text-[0.75rem]">
              Name Accuracy (%){" "}
							{renderTooltipCell(
								replaceTerm("Average name accuracy score (0–100%) across all your drinks."),
								"",
								getCategoryColor("content")
							)}
						</span>
					</div>
				),
				dataIndex: "nameAccuracy",
				key: "nameAccuracy",
				width: 90,
				render: (value: number, record: OneFormulaData) => {
					const canEdit = isEditingOneFormula;
          const isOverrideRow = record.key.endsWith('-override');
          const isDefaultRow = record.key === "default-value";
					return (
						<div className="flex items-center justify-center">
							{canEdit ? (
								<EditableInput
									size="small"
									min={0}
									max={100}
									value={editingOneFormulaData[record.key]?.nameAccuracy ?? value}
									suffix="%"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const numValue = parseInt(e.target.value) || 0;
										handleInputChange(record.key, "nameAccuracy", numValue);
									}}
								/>
							) : (
								<div 
									className="text-center text-xs font-normal px-2.5 py-1 rounded-sm inline-flex justify-center items-center"
									style={isOverrideRow || isDefaultRow ? { fontWeight: 500 } : {fontStyle: "italic"}}
								>
									{value}%
								</div>
							)}
						</div>
					);
				},
			},
			{
				title: (
					<div className="flex gap-1.5 justify-center items-center">
						<span className="text-[0.75rem]">
              Image Presence (%){" "}
							{renderTooltipCell(
								replaceTerm("Average % of your drinks with images (excluding combos)."),
								"",
								getCategoryColor("content")
							)}
						</span>
					</div>
				),
				dataIndex: "avgImagePresence",
				key: "avgImagePresence",
				width: 90,
				render: (value: number, record: OneFormulaData) => {
					const canEdit = isEditingOneFormula;
          const isOverrideRow = record.key.endsWith('-override');
          const isDefaultRow = record.key === "default-value";
					return (
						<div className="flex items-center justify-center">
							{canEdit ? (
								<EditableInput
									size="small"
									min={0}
									max={100}
									value={
										editingOneFormulaData[record.key]?.avgImagePresence ?? value
									}
									suffix="%"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const numValue = parseInt(e.target.value) || 0;
										handleInputChange(record.key, "avgImagePresence", numValue);
									}}
								/>
							) : (
								<div 
									className="text-center text-xs font-normal px-2.5 py-1 rounded-sm inline-flex justify-center items-center"
									style={isOverrideRow || isDefaultRow ? { fontWeight: 500 } : {fontStyle: "italic"}}
								>
									{value}%
								</div>
							)}
						</div>
					);
				},
			},
		],
		onHeaderCell: () => ({
			style: {
				...tableTitleStyle,
				backgroundColor: getCategoryColor("content"),
				borderTopRightRadius: "12px",
			},
		}),
	},
	{
		title: (
			<div className="flex items-center gap-1 justify-center px-3">Edit by</div>
		),
		dataIndex: "editBy",
		key: "editBy",
		width: 150,
		render: (text: string, record: OneFormulaData) => (
			<div className="flex items-center">
        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            <div
              className="text-white flex items-center justify-center"
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: getCategoryColor("content"),
              }}
            >
              {text ? text.charAt(0).toUpperCase() : "D"}
            </div>
            <span className="text-[0.75rem] text-left">{text || "-"}</span>
          </div>
          {record.lastEditTime && (
            <div className="h-4 text-[0.5rem] text-gray-500 text-left flex items-center">
              Last Edit on {record.lastEditTime}
            </div>
          )}
        </div>
			</div>
		),
	},
	{
		title: "",
		key: "actions",
		width: 40,
		render: (_: any, record: OneFormulaData) => {
			const isDefaultValueRow = record.market === "Default Value";

				if (!isEditingOneFormula) return null;

				return (
				<div className="flex items-center justify-center">
					{isDefaultValueRow ? (
						// 🎯 Default Value row in Edit All mode: "Save Default" + "Apply to All"
						<Dropdown
							menu={{
								items: [
									{
										key: "save-default",
										label: "Save Default",
										onClick: () => handleSaveDefaultValue(record.key),
									},
									{
										key: "apply-to-all",
										label: "Apply to All",
										onClick: () => handleApplyToAllClick(),
									},
								],
							}}
							trigger={["click"]}
							placement="bottomRight"
						>
							<button
								className="text-blue-600 hover:text-blue-400 transition-colors duration-200 bg-transparent border-none cursor-pointer p-1 rounded flex items-center justify-center"
								style={{ width: "24px", height: "24px" }}
								aria-label="Default value options"
							>
								<div className="flex items-center justify-center gap-0.5">
									<div className="w-1 h-1 bg-current rounded-full"></div>
									<div className="w-1 h-1 bg-current rounded-full"></div>
									<div className="w-1 h-1 bg-current rounded-full"></div>
								</div>
							</button>
						</Dropdown>
					) : (
						// Every other row: per-row actions
						<Dropdown
							menu={{
								items: [
									// Applies the default values to this row's editing fields; the
									// user still saves via "Review & Save All".
									{
										key: "apply-default-value",
										label: "Apply default value here",
										onClick: () => handleApplyDefaultToEditingRow(record.key),
									},
								],
							}}
							trigger={["click"]}
							placement="bottomRight"
						>
							<button
								className="text-blue-600 hover:text-blue-400 transition-colors duration-200 bg-transparent border-none cursor-pointer p-1 rounded flex items-center justify-center"
								style={{ width: "24px", height: "24px" }}
								aria-label="More actions"
							>
								<div className="flex items-center justify-center gap-0.5">
									<div className="w-1 h-1 bg-current rounded-full"></div>
									<div className="w-1 h-1 bg-current rounded-full"></div>
									<div className="w-1 h-1 bg-current rounded-full"></div>
								</div>
							</button>
						</Dropdown>
					)}
				</div>
			);
		},
	},
];
};

export { getOneFormulaColumns };
